import {
  Button,
  Checkbox,
  Css,
  MultiSelectField,
  Palette,
  PresentationProvider,
  SelectField,
  StaticField,
  ToggleChipGroup,
  Tooltip,
  useTestIds,
} from "@homebound/beam";
import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Icon } from "src/components";
import { SaveTakeoffLineItemInput, SaveTargetCombinationInput } from "src/generated/graphqlTypes";
import { useRouteParams } from "src/hooks/useRouteParams";
import { disableBasedOnPotentialOperationText } from "src/routes/components/PotentialOperationsUtils";
import { PageHeader } from "src/routes/layout/PageHeader";
import { DesignPackageItemSlotEditorDocument } from "src/routes/libraries/design-catalog/design-package-configurator/components/DesignPackageItemSlotEditor.generated";
import {
  DesignPackageProductCardPage_MaterialVariantQuery,
  DesignPackageProductCardPage_PlaceholderTliFragment,
  DesignPackageProductCardPage_ProductTliQuery,
  useDesignPackageProductCardPage_MaterialVariantQuery,
  useDesignPackageProductCardPage_OptionCombinationsQuery,
  useDesignPackageProductCardPage_ProductTliQuery,
  useDesignPackageProductCardPage_SaveTakeoffLineItemMutation,
} from "src/routes/libraries/design-catalog/design-package-configurator/DesignPackageProductCardPage.generated";
import { DesignPackageParams } from "src/routes/routesDef";
import {
  createDesignPackageProductSearchUrl,
  createDesignPackageUrl,
  createDesignPackagesUrl,
  createMaterialDetailsUrl,
  createPlanPackageTakeoffUrl,
} from "src/RouteUrls";
import { fail, queriesResult } from "src/utils";
import { PRODUCT_FALLBACK_IMG_URL } from "src/utils/constants";
import { h100ExclHdr } from "./DesignPackageConfigurator";
import { DesignPackageConfigurator_DesignPackageFragment } from "./DesignPackageConfigurator.generated";
import { useDesignPackageConfiguratorContext } from "./DesignPackageConfiguratorContext";

/** After selecting a product (or editing an existing product card), configures the product + chip for the given card. */
export function DesignPackageProductCardPage() {
  const { placeholderTliId, productTliId } = useRouteParams<DesignPackageParams>();
  const { designPackage, materialVariantId, readOnly } = useDesignPackageConfiguratorContext();
  const isNoMaterialVariant = materialVariantId === "noMaterialVariant";

  const materialVariantQuery = useDesignPackageProductCardPage_MaterialVariantQuery({
    variables: {
      materialVariantId: materialVariantId!,
      skipMaterialVariant: isNoMaterialVariant,
      placeholderTliId,
      rpavId: designPackage.version.id,
    },
  });
  // We may not have a `productTliId` if going through the "+ New Product Slot" flow
  // (...I'm not including this in the previous query only b/c then we'd have "two takeoffLineItems"
  // in the query, and need to use aliases, which our GraphQL factories don't support that well atm.)
  const productTliQuery = useDesignPackageProductCardPage_ProductTliQuery({
    variables: { productTliId: productTliId!, versionId: designPackage.version.id },
    skip: !productTliId,
  });

  const [saveTli] = useDesignPackageProductCardPage_SaveTakeoffLineItemMutation({
    // reload ItemSlotEditor with any backfilled cards and stolen chips. Much easier than requerying for them via mutation-peel-back.
    refetchQueries: [DesignPackageItemSlotEditorDocument],
  });

  return queriesResult([materialVariantQuery, productTliQuery] as const, {
    data: (materialVariantData, productTliData) => (
      <DesignPackageProductCardPageView
        designPackage={designPackage}
        materialVariantData={materialVariantData}
        productTliData={productTliData}
        saveTli={saveTli}
        isNoMaterialVariant={isNoMaterialVariant}
        readOnly={readOnly}
      />
    ),
  });
}

type DesignPackageProductCardPageViewProps = {
  designPackage: DesignPackageConfigurator_DesignPackageFragment;
  materialVariantData: DesignPackageProductCardPage_MaterialVariantQuery;
  productTliData: DesignPackageProductCardPage_ProductTliQuery | undefined;
  saveTli: ReturnType<typeof useDesignPackageProductCardPage_SaveTakeoffLineItemMutation>[0];
  isNoMaterialVariant: boolean;
  readOnly: boolean;
};
function DesignPackageProductCardPageView({
  designPackage,
  materialVariantData,
  productTliData,
  isNoMaterialVariant,
  readOnly,
  saveTli,
}: DesignPackageProductCardPageViewProps) {
  const tid = useTestIds({});
  const navigate = useNavigate();
  const { designPackageId, placeholderTliId, productTliId } = useRouteParams<DesignPackageParams>();
  const placeholderTli = materialVariantData.takeoffLineItem;
  const planLines = placeholderTli.planLines;
  const item = placeholderTli.item;
  const mv = materialVariantData.materialVariant;

  const {
    designOptionRpogs,
    planRpogs,
    planPackages,
    selectedTargetGroups,
    setSelectedTargetGroups,
    selectedCombinations,
    setSelectedCombinations,
    selectedPlanPackage,
    setSelectedPlanPackage,
  } = useManageOptionSelections(productTliData, placeholderTli);

  const listing = mv?.listing;
  if (!isNoMaterialVariant && (!mv || !listing)) fail("Material Variant has no listing");

  /**
   * Query for all design package combinations given the selected target groups
   * and the placeholder combinations given the user's selection,
   * returning an exhaustive list of non-overlapping combinations for all product cards
   */
  const placeholderCombinationsQuery = useDesignPackageProductCardPage_OptionCombinationsQuery({
    variables: {
      placeholderCombinationsInput: {
        placeholderId: placeholderTliId,
        targetGroupIds: selectedTargetGroups,
        targetCombinations: selectedCombinations,
        productCardId: productTliId,
        targetPlanId: selectedPlanPackage,
      },
      targetGroupIds: selectedTargetGroups,
      designPackageId: designPackage.id,
      designPackageVersionId: designPackage.version.id,
    },
    // Don't if we're not using target groups (e.g., when creating a new product card)
    skip: selectedTargetGroups.isEmpty,
  });

  // Set placeholder combinations when expand/skrink the target groups
  // Skip it for new cards
  useEffect(() => {
    if (productTliId && placeholderCombinationsQuery.data) {
      const placeholderCombos = placeholderCombinationsQuery.data.placeholderCombinations.find(
        (c) => c.takeoffLineItem.id === productTliId,
      );
      if (placeholderCombos) {
        setSelectedCombinations(placeholderCombos.combinations.map((c) => ({ options: c.options.map((o) => o.id) })));
      }
    }
  }, [placeholderCombinationsQuery.data, productTliId, setSelectedCombinations]);

  const placeholderCombinations = placeholderCombinationsQuery.data?.placeholderCombinations ?? [];
  const designPackageCombinations = placeholderCombinationsQuery.data?.designPackage?.combinations ?? [];
  const missingCombinationIds = placeholderTli.missingCombinations.map((c) => c.id);

  return (
    <div css={{ ...Css.df.fdc.$, ...h100ExclHdr }}>
      <PageHeader
        title="Product Details"
        breadcrumb={[
          { href: createDesignPackagesUrl(), label: "Design Packages" },
          { href: createDesignPackageUrl(designPackage.id, designPackage.version.id), label: designPackage.name },
        ]}
        backButton={() => navigate(-1)}
        xss={Css.p3.$}
      />
      <div css={Css.df.jcc.bgGray100.oya.$}>
        <div css={Css.df.fdc.gap4.maxwPx(1040).mbPx(80).$}>
          <div css={Css.xl2.$}>Select product for {item?.name}</div>
          {isNoMaterialVariant ? (
            <>
              <div css={Css.xl2.$}>Apply Options to Empty Slot for {item?.name}</div>
              <div>
                <div css={Css.df.fdr.aifs.py3.gap4.$}>
                  <div css={Css.h100.mhPx(338).wPx(300).df.fdc.aic.jcc.gap1.tac.ba.bcGray700.br8.bshBasic.$}>
                    <Icon /* Not a CTA */ icon="remove" color={Palette.Gray700} inc={4} />
                    <div css={Css.gray700.mdSb.maxw50.$}>Make this an empty Product Slot</div>
                  </div>
                  <div css={Css.wPx(400).df.fdc.gap1.$}>
                    <span>
                      Sometimes a product type isn't offered with certain Package Options (e.g., no refrigerator with
                      the Essential package). In these cases, tell Design Packages to leave it empty!
                    </span>
                    <span>Specify which Package Options won't offer a {item?.name}.</span>
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div css={Css.xl2.$}>Product Details</div>
              <div>
                <div css={Css.xl.$}>{listing?.name ?? "no name?"}</div>
                <div css={Css.df.fdr.aifs.py3.gap4.$}>
                  <div>
                    <img
                      src={mv?.featuredImage?.asset?.previewUrl ?? PRODUCT_FALLBACK_IMG_URL}
                      alt={`${listing?.name}-product`}
                      css={Css.h100.wPx(300).objectCover.oh.$}
                    />
                  </div>
                  <div css={Css.wPx(400).df.fdc.gap1.$}>
                    <PresentationProvider fieldProps={{ labelStyle: "left" }}>
                      {mv?.id && listing?.id && (
                        <Link to={createMaterialDetailsUrl(listing.id, { mv: mv.id })} target="_blank">
                          <div css={Css.df.gap1.blue500.smSb.cursorPointer.onHover.blue600.$}>
                            <Icon icon="linkExternal" inc={2.5} />
                            View in Material Catalog
                          </div>
                        </Link>
                      )}
                      <StaticField label="Manufacturer" value={listing?.brand?.name} />
                      <StaticField label="SKU/Model #" value={mv?.modelNumber ?? "n/a"} />
                      <StaticField label="SKU/Model #" value={mv?.modelNumber ?? "n/a"} />
                      {mv?.materialAttributeValues.map((mav) => (
                        // NOTE: This field might need to be updated to handle range values
                        //    SC-55230
                        <StaticField key={mav.id} label={mav.dimension.name} value={mav?.textValue ?? undefined} />
                      ))}

                      {mv?.designFeedback && (
                        <StaticField label="Design Feedback">
                          <div dangerouslySetInnerHTML={{ __html: mv.designFeedback }} />
                        </StaticField>
                      )}
                    </PresentationProvider>
                  </div>
                </div>
              </div>
            </>
          )}
          <div /* Divider */ css={Css.bb.w100.bcGray300.$} />
          <div css={Css.df.fdc.gap2.$}>
            <div css={Css.xl2.$}>Apply To:</div>
            {designOptionRpogs.isEmpty && planRpogs.isEmpty && <div css={Css.asc.xl.$}>No options available</div>}
            {designOptionRpogs.nonEmpty && (
              <>
                <ToggleChipGroup
                  label={""}
                  {...tid.targetGroupChipGroup}
                  options={designOptionRpogs
                    .sortBy((rpog) => rpog.order)
                    .map((rpog) => ({ label: rpog.name, value: rpog.id }))}
                  values={selectedTargetGroups}
                  onChange={(rpogIds) => setSelectedTargetGroups(rpogIds)}
                />
                <MultiSelectField
                  {...tid.combinationsMultiSelectField}
                  label={
                    selectedTargetGroups.isEmpty
                      ? "--"
                      : designOptionRpogs
                          .filter((rpog) => selectedTargetGroups.includes(rpog.id))
                          .map((rpog) => rpog.name)
                          .join(" + ") + " Pairings"
                  }
                  options={designPackageCombinations}
                  disabled={selectedTargetGroups.isEmpty}
                  getOptionValue={(option) => option.id}
                  getOptionMenuLabel={(option) => {
                    if (!missingCombinationIds.includes(option.id)) return option.name;
                    return (
                      <div css={Css.df.aifs.gap1.$}>
                        {option.name}
                        <Tooltip title="Missing combination">
                          <Icon icon="infoCircle" color={Palette.Red300} />
                        </Tooltip>
                      </div>
                    );
                  }}
                  placeholder={selectedCombinations.isEmpty ? "None Selected" : undefined}
                  onSelect={(val) => setSelectedCombinations(val.map((v) => ({ options: v.split(",") })))}
                  values={selectedCombinations.map((c) => c.options.join(","))}
                />
                <Checkbox
                  label="Select All"
                  selected={
                    selectedCombinations.nonEmpty && selectedCombinations.length === designPackageCombinations?.length
                  }
                  disabled={selectedTargetGroups.isEmpty}
                  onChange={(selected) => {
                    if (selected && designPackageCombinations.nonEmpty) {
                      const options = designPackageCombinations.map((c) => ({ options: c.options.map((o) => o.id) }));
                      setSelectedCombinations(options);
                    } else {
                      setSelectedCombinations([]);
                    }
                  }}
                />
              </>
            )}
            <SelectField
              label="Plan Package"
              placeholder="All Plans"
              options={[{ id: undefined, name: "All Plans" }, ...planPackages]}
              value={selectedPlanPackage}
              getOptionValue={(pp) => pp.id}
              getOptionMenuLabel={(pp) => pp.name}
              onSelect={(ppId) => setSelectedPlanPackage(ppId ?? null)}
            />
            <div css={Css.df.fdc.$}>
              <div css={Css.gray700.mb1.$}>Plan lines</div>
              {planLines?.nonEmpty ? (
                planLines.map((tli) => (
                  <div key={tli.id} css={Css.gray900.sm.df.pyPx(4).my1.$}>
                    <Link
                      {...tid.planLineLink}
                      to={`${createPlanPackageTakeoffUrl(tli.readyPlan.id, tli.readyPlan.version.id)}?search=${tli.item.fullCode}`}
                      target="_blank"
                    >
                      {[tli.readyPlan.name, tli.location.displayLocationPath, tli.name].join(" - ")}
                    </Link>
                  </div>
                ))
              ) : (
                <div>No plan lines associated to this slot</div>
              )}
            </div>
          </div>
        </div>
        <div
          /* BottomPanel  */ css={Css.absolute.bottom0.w100.df.fdr.jcfe.aic.p4.bgWhite.bt.bcGray300.gap2.sm.hPx(80).$}
        >
          <Button
            variant="textSecondary"
            label="Back to Product Search"
            onClick={createDesignPackageProductSearchUrl(
              designPackage.id,
              designPackage.version.id,
              placeholderTliId,
              productTliId,
            )}
            size="lg"
            disabled={readOnly}
          />
          {productTliId && (
            <Button
              variant="tertiaryDanger"
              label="Clear Product Selection"
              onClick={async () => {
                const result = await saveTli({
                  variables: { readyPlanId: designPackage.id, input: [{ id: productTliId, bidItemId: null }] },
                });
                const versionId = result.data?.saveTakeoffLineItems.version.id ?? fail();
                navigate(createDesignPackageUrl(designPackageId, versionId), { replace: true });
              }}
              size="lg"
              disabled={disableBasedOnPotentialOperationText(designPackage.canEdit) || readOnly}
            />
          )}
          <Button
            variant="quaternary"
            label="Cancel"
            onClick={() => navigate(createDesignPackageUrl(designPackageId, designPackage.version.id))}
            size="lg"
          />
          <Button
            label="Add Product"
            onClick={async () => {
              const inputs: SaveTakeoffLineItemInput[] = [
                {
                  ...(isNoMaterialVariant
                    ? { isDisabledMaterialVariant: true }
                    : // We should only be saving material variant ID because we want to allow different UOMs
                      { materialVariantId: mv?.id ?? fail("unable to determine material variant ID to save") }),
                  targetCombinations: selectedCombinations,
                  targetPlanId: selectedPlanPackage,
                  // If we have a product Tli update it, otherwise create new
                  ...(productTliId
                    ? // UPDATE
                      { id: productTliId }
                    : // CREATE
                      { placeholderId: placeholderTliId }),
                },
              ];

              // Push the placeholder TLI and its target groups
              // And the target combinations for each placeholder TLI
              if (selectedTargetGroups.nonEmpty) {
                // The placeholder TLI holds the target groups for all product cards
                inputs.push({ id: placeholderTliId, targetGroupIds: selectedTargetGroups });
                // The placeholderCombinations query will have the ideal combinations for each product card given the current selections
                // Therefore we need to exhaustively save all TLIs
                placeholderCombinations
                  .filter(({ takeoffLineItem }) => takeoffLineItem.id !== productTliId)
                  .forEach(({ takeoffLineItem, combinations }) => {
                    const combo = combinations.map((combo) => ({ options: combo.options.map((o) => o.id) }));
                    inputs.push({ id: takeoffLineItem.id, targetCombinations: combo });
                  });
              }

              const result = await saveTli({ variables: { readyPlanId: designPackage.id, input: inputs } });
              const versionId = result.data?.saveTakeoffLineItems.version.id ?? fail();
              navigate(createDesignPackageUrl(designPackageId, versionId), { replace: true });
            }}
            disabled={
              disableBasedOnPotentialOperationText(designPackage.canEdit) ||
              readOnly ||
              placeholderCombinationsQuery.loading
            }
            size="lg"
          />
        </div>
      </div>
    </div>
  );
}

/** Manages some quirks around Options */
function useManageOptionSelections(
  productTliQuery: DesignPackageProductCardPage_ProductTliQuery | undefined,
  placeholderTli: DesignPackageProductCardPage_PlaceholderTliFragment,
) {
  const { designPackage } = useDesignPackageConfiguratorContext();
  const [selectedTargetGroups, setSelectedTargetGroups] = useState<string[]>([]);
  const [selectedCombinations, setSelectedCombinations] = useState<SaveTargetCombinationInput[]>([]);
  const [selectedPlanPackage, setSelectedPlanPackage] = useState<string | null>(null);

  const [forDesignRpogs, planRpogs] = useMemo(() => {
    return [
      designPackage.optionGroups.filter((rpog) => rpog.forDesignPackages),
      designPackage.optionGroups.filter((rpog) => rpog.isPlanPackage),
    ];
  }, [designPackage.optionGroups]);

  /** Re-init currently-selected combinations/targetGroups from productTli when its query resolves */
  useEffect(() => {
    const targetCombinations = productTliQuery?.takeoffLineItem?.targetCombinations ?? [];
    setSelectedTargetGroups(placeholderTli.targetGroups.map((rpog) => rpog.id));
    setSelectedCombinations(targetCombinations.map((combo) => ({ options: combo.options.map((rpo) => rpo.id) })));
    setSelectedPlanPackage(productTliQuery?.takeoffLineItem.targetPlan?.id ?? null);
  }, [productTliQuery, placeholderTli.targetGroups]);

  return {
    designOptionRpogs: forDesignRpogs,
    planRpogs,
    planPackages: designPackage.planPackages,
    selectedTargetGroups,
    setSelectedTargetGroups,
    selectedCombinations,
    setSelectedCombinations,
    selectedPlanPackage,
    setSelectedPlanPackage,
  };
}
